import { useState, type ReactNode } from 'react';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * Local placeholder primitives for the rail cards. These mirror the eventual
 * shape of `editor-chrome/ui/{Pill,Section,Card}.tsx` exports from
 * shell-builder. When those land, swap the imports in each card to:
 *   import { Pill, Section, Card } from '../ui';
 */

export type PillTone = 'neutral' | 'success' | 'warning' | 'danger' | 'coral';
export type PillSize = 'sm' | 'md';

export interface PillProps {
  tone?: PillTone;
  size?: PillSize;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
}

const PILL_TONE: Record<PillTone, string> = {
  neutral: 'bg-warm-bg text-muted-foreground border-border',
  success: 'bg-teal-light text-teal-dark border-teal/30',
  warning: 'bg-[hsl(var(--status-warning-bg))] text-[hsl(var(--status-warning))] border-[hsl(var(--status-warning)/0.3)]',
  danger: 'bg-[hsl(var(--status-error-bg))] text-[hsl(var(--status-error))] border-[hsl(var(--status-error)/0.3)]',
  coral: 'bg-coral-light text-coral-dark border-coral/30',
};

export function Pill({ tone = 'neutral', size = 'sm', icon, children, className }: PillProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 font-medium border rounded-md',
        size === 'sm' ? 'px-1.5 py-0.5 text-[11px]' : 'px-2.5 py-1 text-[12px]',
        PILL_TONE[tone],
        className,
      )}
    >
      {icon && <span className="inline-flex">{icon}</span>}
      {children}
    </span>
  );
}

export interface CardProps {
  children: ReactNode;
  className?: string;
  /** Stable id used to scroll to the card (jump-to-card from mini rail). */
  railSection?: string;
}

export function Card({ children, className, railSection }: CardProps) {
  return (
    <div
      data-rail-card
      data-rail-section={railSection}
      className={cn(
        'bg-card border border-border rounded-lg overflow-hidden',
        className,
      )}
    >
      {children}
    </div>
  );
}

export interface SectionProps {
  title: string;
  hint?: string;
  score?: number;
  defaultOpen?: boolean;
  children: ReactNode;
}

/** Collapsible section with optional score badge — used inside ContentQualityCard. */
export function Section({ title, hint, score, defaultOpen = true, children }: SectionProps) {
  const [open, setOpen] = useState(defaultOpen);
  const tone: PillTone =
    score == null ? 'neutral' : score >= 80 ? 'success' : score >= 60 ? 'warning' : 'danger';
  return (
    <div className="border-t border-border">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 bg-transparent border-0 cursor-pointer text-left hover:bg-warm-bg/50 transition-colors"
      >
        <ChevronDown
          className={cn(
            'size-3.5 text-muted-foreground transition-transform',
            open ? 'rotate-0' : '-rotate-90',
          )}
        />
        <span className="flex-1 text-[12px] font-semibold text-foreground">{title}</span>
        {hint && <span className="text-[10.5px] text-muted-foreground">{hint}</span>}
        {score != null && (
          <Pill tone={tone} size="sm" className="font-mono font-bold">
            {score}
          </Pill>
        )}
      </button>
      {open && <div className="px-3 pb-3 pl-9">{children}</div>}
    </div>
  );
}

export interface CardHeaderProps {
  icon?: ReactNode;
  title: string;
  trailing?: ReactNode;
  className?: string;
}

/** Standard rail-card header row: icon + title + trailing slot. */
export function CardHeader({ icon, title, trailing, className }: CardHeaderProps) {
  return (
    <div className={cn('flex items-center gap-2 px-3 pt-3 pb-2', className)}>
      {icon && <span className="inline-flex text-muted-foreground">{icon}</span>}
      <div className="text-[12px] font-bold text-foreground">{title}</div>
      <div className="flex-1" />
      {trailing}
    </div>
  );
}
